import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PortfolioItemModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingItem, 
  isLoading, 
  categories = [], // Provide default empty array
  existingItems = [] // Add existing items for duplicate validation
}) => {
  // Ensure categories is always an array and handle potential undefined/null values
  const safeCategories = useMemo(() => {
    if (!categories || !Array.isArray(categories)) {
      return [];
    }
    return categories.filter(cat => cat && typeof cat === 'string');
  }, [categories]);
  
  // Initialize form data with safe defaults
  const getInitialFormData = useCallback(() => {
    return {
      title: '',
      category: safeCategories.length > 0 ? safeCategories[0] : '',
      type: 'project',
      description: '',
      fullDescription: '',
      client: '',
      completedDate: '',
      duration: '',
      clientRating: 5,
      technologies: [],
      liveUrl: '',
      sourceUrl: '',
      results: [],
      images: [],
      thumbnail: '',
      isPublic: true,
      isFeatured: false
    };
  }, [safeCategories]);
  
  const [formData, setFormData] = useState(getInitialFormData);

  const [newTechnology, setNewTechnology] = useState('');
  const [newResult, setNewResult] = useState('');
  const [errors, setErrors] = useState({});

  const projectTypes = [
    { value: 'project', label: 'Project' },
    { value: 'website', label: 'Website' },
    { value: 'document', label: 'Document' },
    { value: 'design', label: 'Design' },
    { value: 'social', label: 'Social Media' },
    { value: 'video', label: 'Video' },
    { value: 'other', label: 'Other' }
  ];

  // Helper function to handle both snake_case and camelCase fields
  const getFieldValue = (item, snakeCase, camelCase, defaultValue) => {
    // First check snake_case version, then camelCase version
    if (item[snakeCase] !== undefined) return item[snakeCase];
    if (item[camelCase] !== undefined) return item[camelCase];
    return defaultValue;
  };

  useEffect(() => {
    if (editingItem) {
      // Handle both snake_case and camelCase field names
      setFormData({
        id: editingItem.id,
        title: editingItem.title || '',
        category: editingItem.category || (safeCategories.length > 0 ? safeCategories[0] : ''),
        type: editingItem.type || 'project',
        description: editingItem.description || '',
        fullDescription: editingItem.full_description || '',
        client: editingItem.client || '',
        completedDate: (() => {
          const dateValue = getFieldValue(editingItem, 'completed_date', 'completedDate', '');
          if (!dateValue) return '';
          const dateStr = typeof dateValue === 'string' ? dateValue : dateValue.toString();
          return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        })(),
        duration: editingItem.duration || '',
        clientRating: getFieldValue(editingItem, 'client_rating', 'clientRating', 5),
        technologies: Array.isArray(editingItem.technologies) ? editingItem.technologies : [],
        liveUrl: getFieldValue(editingItem, 'project_url', 'liveUrl', ''),
        sourceUrl: getFieldValue(editingItem, 'github_url', 'sourceUrl', ''),
        results: Array.isArray(editingItem.results) ? editingItem.results : [],
        images: Array.isArray(editingItem.images) ? editingItem.images : [],
        thumbnail: getFieldValue(editingItem, 'image_url', 'thumbnail', ''),
        isPublic: getFieldValue(editingItem, 'is_public', 'isPublic', true),
        isFeatured: getFieldValue(editingItem, 'is_featured', 'isFeatured', false)
      });
    } else {
      setFormData({
        title: '',
        category: safeCategories.length > 0 ? safeCategories[0] : '',
        type: 'project',
        description: '',
        fullDescription: '',
        client: '',
        completedDate: '',
        duration: '',
        clientRating: 5,
        technologies: [],
        liveUrl: '',
        sourceUrl: '',
        results: [],
        images: [],
        thumbnail: '',
        isPublic: true,
        isFeatured: false
      });
    }
    setErrors({});
  }, [editingItem, isOpen, safeCategories]);

  const handleInputChange = (field, value) => {
    // Handle case when we're updating newTechnology or newResult
    if (field === 'newTechnology') {
      setNewTechnology(value);
      return;
    }
    if (field === 'newResult') {
      setNewResult(value);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleImageUpload = (event) => {
    // Handle both event object and direct files array
    const files = Array.from(event?.target?.files || event?.files || event || []);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setErrors(prev => ({
          ...prev,
          images: 'Each image must be less than 10MB'
        }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result;
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, imageUrl],
          thumbnail: prev.thumbnail || imageUrl // Use first image as thumbnail if none set
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove),
      thumbnail: prev.images[indexToRemove] === prev.thumbnail ? (prev.images[0] || '') : prev.thumbnail
    }));
  };

  const handleSetThumbnail = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      thumbnail: imageUrl
    }));
  };

  const handleAddTechnology = () => {
    // Convert to string and handle potential objects
    const techValue = newTechnology ? 
      (typeof newTechnology === 'object' ? 
        (newTechnology.toString ? newTechnology.toString() : '') : 
        String(newTechnology)) : 
      '';
    
    if (techValue.trim() && !formData.technologies.includes(techValue.trim())) {
      setFormData(prev => ({
        ...prev,
        technologies: [...prev.technologies, techValue.trim()]
      }));
      setNewTechnology('');
    }
  };

  const handleRemoveTechnology = (tech) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter(t => t !== tech)
    }));
  };

  const handleAddResult = () => {
    // Convert to string and handle potential objects
    const resultValue = newResult ? 
      (typeof newResult === 'object' ? 
        (newResult.toString ? newResult.toString() : '') : 
        String(newResult)) : 
      '';
    
    if (resultValue.trim() && !formData.results.includes(resultValue.trim())) {
      setFormData(prev => ({
        ...prev,
        results: [...prev.results, resultValue.trim()]
      }));
      setNewResult('');
    }
  };

  const handleRemoveResult = (result) => {
    setFormData(prev => ({
      ...prev,
      results: prev.results.filter(r => r !== result)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters long';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    } else {
      // Check for duplicate titles (excluding current item when editing)
      const duplicateTitle = existingItems.find(item => 
        item.title?.toLowerCase().trim() === formData.title.toLowerCase().trim() && 
        item.id !== formData.id
      );
      if (duplicateTitle) {
        newErrors.title = 'A portfolio item with this title already exists';
      }
    }

    // Description validation
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    // Category validation
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    // Client rating validation
    if (formData.clientRating && (formData.clientRating < 1 || formData.clientRating > 5)) {
      newErrors.clientRating = 'Rating must be between 1 and 5';
    }

    // URL validation
    if (formData.liveUrl && !isValidUrl(formData.liveUrl)) {
      newErrors.liveUrl = 'Please enter a valid URL';
    }
    if (formData.sourceUrl && !isValidUrl(formData.sourceUrl)) {
      newErrors.sourceUrl = 'Please enter a valid URL';
    }

    // Duration validation
    if (formData.duration && formData.duration.trim().length > 50) {
      newErrors.duration = 'Duration must be less than 50 characters';
    }

    // Client name validation
    if (formData.client && formData.client.trim().length > 100) {
      newErrors.client = 'Client name must be less than 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      // Map form fields to match database schema with snake_case field names
      const saveData = {
        id: formData.id,
        title: typeof formData.title === 'string' ? formData.title.trim() : String(formData.title || ''),
        description: typeof formData.description === 'string' ? formData.description.trim() : String(formData.description || ''),
        category: formData.category || '',
        type: formData.type || 'project',
        project_url: typeof formData.liveUrl === 'string' ? formData.liveUrl.trim() : (typeof formData.liveUrl === 'object' ? '' : (formData.liveUrl ? String(formData.liveUrl) : null)),
        github_url: typeof formData.sourceUrl === 'string' ? formData.sourceUrl.trim() : (typeof formData.sourceUrl === 'object' ? '' : (formData.sourceUrl ? String(formData.sourceUrl) : null)),
        technologies: Array.isArray(formData.technologies) ? formData.technologies : [],
        image_url: formData.thumbnail || null,
        full_description: typeof formData.fullDescription === 'string' ? formData.fullDescription.trim() : String(formData.fullDescription || ''),
        client: typeof formData.client === 'string' ? formData.client.trim() : (typeof formData.client === 'object' ? '' : (formData.client ? String(formData.client) : null)),
        completed_date: formData.completedDate || null,
        duration: typeof formData.duration === 'string' ? formData.duration.trim() : (typeof formData.duration === 'object' ? '' : (formData.duration ? String(formData.duration) : null)),
        client_rating: Number(formData.clientRating) || 0,
        results: Array.isArray(formData.results) ? formData.results : [],
        images: Array.isArray(formData.images) ? formData.images.map(img => typeof img === 'string' ? img : '') : [],
        is_public: formData.isPublic === true || formData.isPublic === 'true',
        is_featured: formData.isFeatured === true || formData.isFeatured === 'true'
      };
      
      // Keep any existing ID if editing
      if (formData.id) {
        saveData.id = formData.id;
      }
      
      onSave(saveData);
    }
  };

  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-surface rounded-lg w-full max-w-2xl sm:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
            <h2 className="text-xl sm:text-2xl font-bold text-text-primary truncate">
              {editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}
            </h2>
            <button
              onClick={onClose}
              className="flex-shrink-0 text-text-muted hover:text-text-primary"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
          
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <h3 className="text-lg font-semibold text-text-primary mb-6">Basic Information</h3>
              
              {/* Title - Full width */}
              <div className="mb-6">
                <Input
                  label="Project Title"
                  value={formData.title ? (typeof formData.title === 'object' ? '' : String(formData.title)) : ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter project title"
                  error={errors.title}
                  required
                />
              </div>

              {/* Category, Project Type, Client - Responsive grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <Select
                  label="Category"
                  value={formData.category || ''}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  options={safeCategories}
                  error={errors.category}
                  required
                  placeholder="Select category"
                  helperText="Choose the primary category"
                />
                <Select
                  label="Project Type"
                  value={formData.type || ''}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  options={projectTypes}
                  placeholder="Select type"
                  helperText="Specify the project nature"
                />
                <Input
                  label="Client"
                  value={formData.client ? (typeof formData.client === 'object' ? '' : String(formData.client)) : ''}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  placeholder="Enter client name"
                />
              </div>

              {/* Completed Date, Duration, Rating - Responsive grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Input
                  label="Completed Date"
                  type="date"
                  value={formData.completedDate ? 
                    (typeof formData.completedDate === 'object' ? 
                      (formData.completedDate instanceof Date ? 
                        formData.completedDate.toISOString().split('T')[0] : '') : 
                      String(formData.completedDate)) : 
                    ''}
                  onChange={(e) => handleInputChange('completedDate', e.target.value)}
                />
                <Input
                  label="Project Duration"
                  value={formData.duration ? (typeof formData.duration === 'object' ? '' : String(formData.duration)) : ''}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  placeholder="e.g., 6 weeks"
                  helperText="How long did it take?"
                />
                <Input
                  label="Client Rating"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.clientRating ? (typeof formData.clientRating === 'object' ? 5 : formData.clientRating) : 5}
                  onChange={(e) => handleInputChange('clientRating', parseInt(e.target.value, 10) || 5)}
                  error={errors.clientRating}
                  helperText="Rating 1-5 stars"
                />
              </div>
            </div>

            {/* Descriptions */}
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">Project Description</h3>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Short Description <span className="text-error">*</span>
              </label>
              <textarea
                value={formData.description ? 
                  (typeof formData.description === 'object' ? 
                    (formData.description.toString ? formData.description.toString() : '') : 
                    String(formData.description)) : 
                  ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of the project (1-2 sentences)"
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
                  errors.description ? 'border-error' : 'border-border'
                }`}
                rows={2}
              />
              {errors.description && (
                <p className="text-error text-sm mt-1">{errors.description}</p>
              )}
            </div>

            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Full Description
              </label>
              <textarea
                value={formData.fullDescription ? 
                  (typeof formData.fullDescription === 'object' ? 
                    (formData.fullDescription.toString ? formData.fullDescription.toString() : '') : 
                    String(formData.fullDescription)) : 
                  ''}
                onChange={(e) => handleInputChange('fullDescription', e.target.value)}
                placeholder="Detailed description of the project, challenges, solutions, and process"
                className="w-full px-3 py-2 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                rows={4}
              />
            </div>

            {/* Technologies */}
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">Technologies & Skills</h3>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Technologies & Tools Used
              </label>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <Input
                  value={newTechnology ? (typeof newTechnology === 'object' ? '' : String(newTechnology)) : ''}
                  onChange={(e) => setNewTechnology(e.target.value)}
                  placeholder="Add a technology"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTechnology()}
                />
                <Button
                  variant="outline"
                  iconName="Plus"
                  onClick={handleAddTechnology}
                  disabled={!String(newTechnology || '').trim()}
                  className="sm:flex-shrink-0"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.technologies?.map((tech, index) => {
                  // Convert tech to a safe string representation
                  const techString = tech ? 
                    (typeof tech === 'object' ? 
                      (tech.toString && tech.toString() !== '[object Object]' ? 
                        tech.toString() : 
                        '') : 
                      String(tech)) : 
                    '';
                  
                  // Only render non-empty tech strings
                  return techString ? (
                    <div
                      key={index}
                      className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      <span>{techString}</span>
                      <button
                        onClick={() => handleRemoveTechnology(tech)}
                        className="text-primary-500 hover:text-primary-700"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>

            {/* Images */}
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">Project Images</h3>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <label className="block text-sm font-medium text-text-primary">
                  Project Images
                </label>
                <span className="text-xs text-text-secondary">
                  First image will be used as thumbnail
                </span>
              </div>
              <div className="border-2 border-dashed border-primary-100 rounded-lg p-4 sm:p-6 text-center hover:border-primary-300 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e)}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer inline-flex items-center gap-2 text-primary hover:text-primary-600"
                >
                  <Icon name="Upload" size={20} />
                  <span>Click to upload images</span>
                </label>
                <p className="text-text-muted text-sm mt-2">
                  PNG, JPG up to 10MB each. You can upload multiple images.
                </p>
              </div>
              {errors.images && (
                <p className="text-error text-sm mt-1">{errors.images}</p>
              )}

              {/* Image Preview Grid */}
              {formData.images?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden border border-border shadow-sm">
                        <Image
                          src={image}
                          alt={`Project image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Image Controls */}
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          iconName={formData.thumbnail === image ? "Star" : "StarEmpty"}
                          onClick={() => handleSetThumbnail(image)}
                          className="!p-2"
                          title={formData.thumbnail === image ? "Current thumbnail" : "Set as thumbnail"}
                        />
                        <Button
                          variant="error"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleRemoveImage(index)}
                          className="!p-2"
                          title="Remove image"
                        />
                      </div>
                      
                      {/* Thumbnail Badge */}
                      {formData.thumbnail === image && (
                        <div className="absolute top-2 left-2 bg-warning text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm">
                          Thumbnail
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Results */}
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">Key Results & Achievements</h3>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Key Results & Achievements
              </label>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <Input
                  value={newResult ? (typeof newResult === 'object' ? '' : String(newResult)) : ''}
                  onChange={(e) => setNewResult(e.target.value)}
                  placeholder="Add a result or achievement"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddResult()}
                />
                <Button
                  variant="outline"
                  iconName="Plus"
                  onClick={handleAddResult}
                  disabled={!String(newResult || '').trim()}
                  className="sm:flex-shrink-0"
                >
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.results?.map((result, index) => {
                  // Convert result to a safe string representation
                  const resultString = result ? 
                    (typeof result === 'object' ? 
                      (result.toString && result.toString() !== '[object Object]' ? 
                        result.toString() : 
                        (result.metric && result.value ? 
                          `${result.metric}: ${result.value}` : 
                          '')) : 
                      String(result)) : 
                    '';
                  
                  // Only render non-empty result strings
                  return resultString ? (
                    <div
                      key={index}
                      className="bg-success-50 text-success-700 px-3 py-2 rounded-lg text-sm flex items-center justify-between"
                    >
                      <span className="line-clamp-1">{resultString}</span>
                      <button
                        onClick={() => handleRemoveResult(result)}
                        className="text-success-500 hover:text-success-700 flex-shrink-0 ml-2"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>

            {/* URLs */}
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">Project Links</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
                <Input
                  label="Live URL"
                  type="url"
                  value={formData.liveUrl ? (typeof formData.liveUrl === 'object' ? '' : String(formData.liveUrl)) : ''}
                  onChange={(e) => handleInputChange('liveUrl', e.target.value)}
                  placeholder="https://example.com"
                  error={errors.liveUrl}
                />
                <Input
                  label="Source URL"
                  type="url"
                  value={formData.sourceUrl ? (typeof formData.sourceUrl === 'object' ? '' : String(formData.sourceUrl)) : ''}
                  onChange={(e) => handleInputChange('sourceUrl', e.target.value)}
                  placeholder="https://github.com/username/project"
                  error={errors.sourceUrl}
                />
              </div>
            </div>

            {/* Settings */}
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6 space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary">Visibility Settings</h3>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-white rounded-lg border border-border">
                <div className="flex-1">
                  <div className="font-medium text-text-primary text-sm sm:text-base">Public Portfolio Item <span className="text-xs text-warning-500 font-normal">(Recommended)</span></div>
                  <div className="text-xs sm:text-sm text-text-secondary">Make this item visible in your public portfolio</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 bg-white rounded-lg border border-border">
                <div className="flex-1">
                  <div className="font-medium text-text-primary text-sm sm:text-base">Featured Item</div>
                  <div className="text-xs sm:text-sm text-text-secondary">Highlight this item in your portfolio</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
            <div className="text-xs sm:text-sm text-text-secondary italic order-2 sm:order-1">
              <span className="text-error">*</span> Required fields
            </div>
            <div className="flex gap-3 order-1 sm:order-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                iconName="Save"
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 sm:flex-none"
              >
                {isLoading ? 'Saving...' : (editingItem ? 'Update Item' : 'Add Item')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioItemModal;
